import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp, Loader2, ArrowRight } from "lucide-react";

const BG = "https://images.pexels.com/photos/11376239/pexels-photo-11376239.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(`Welcome, ${data.name.split(" ")[0]}`);
      navigate(data.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative z-10">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden bg-[#15803d]">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#15803d] via-[#15803d]/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-white/15 backdrop-blur flex items-center justify-center">
              <TrendingUp className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl">Futions Investments</span>
          </div>
          <div>
            <h1 className="font-serif text-5xl leading-[1.05] mb-4">Wealth, viewed<br />with clarity.</h1>
            <p className="text-white/70 max-w-sm text-base leading-relaxed">
              A private portal for your portfolio performance, holdings, transactions and statements — all in one refined place.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#f8f9fa]">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-[#15803d] flex items-center justify-center text-white">
              <TrendingUp className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl text-[#15803d]">Futions Investments</span>
          </div>

          <p className="overline mb-2">{mode === "login" ? "Client & Advisor Access" : "Create Account"}</p>
          <h2 className="font-serif text-4xl text-[#0f172a] mb-8">
            {mode === "login" ? "Sign in to your portal" : "Join Futions"}
          </h2>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="name-input" value={form.name} onChange={upd("name")} required
                  className="bg-white focus-visible:ring-2 focus-visible:ring-[#15803d]/20" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{mode === "login" ? "Email or username" : "Email"}</Label>
              <Input id="email" type={mode === "login" ? "text" : "email"} data-testid="email-input" value={form.email} onChange={upd("email")} required
                className="bg-white focus-visible:ring-2 focus-visible:ring-[#15803d]/20" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="password-input" value={form.password} onChange={upd("password")} required
                className="bg-white focus-visible:ring-2 focus-visible:ring-[#15803d]/20" />
            </div>

            <Button type="submit" data-testid="submit-btn" disabled={loading}
              className="w-full h-11 rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="flex items-center gap-2">{mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></span>
              )}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button variant="outline" data-testid="google-login-btn" onClick={googleLogin}
            className="w-full h-11 rounded-full bg-white border-slate-300 hover:bg-slate-50 transition-colors duration-200">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>

          <p className="text-center text-sm text-slate-500 mt-8">
            {mode === "login" ? "New to Futions? " : "Already have an account? "}
            <button data-testid="toggle-mode-btn" onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#15803d] font-semibold hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
